import { RefObject, useEffect } from "react";

/**
 * Options for scroll indicator behavior.
 */
export interface UseScrollIndicatorOptions {
  /**
   * Whether the scroll indicator functionality is active.
   */
  isActive: boolean;

  /**
   * Ref to the scroll indicator element.
   */
  indicatorRef: RefObject<HTMLElement>;

  /**
   * Optional ref to the scrollable content element.
   * If not provided, will look for [data-modal-scrollable-content].
   */
  scrollableRef?: RefObject<HTMLElement>;

  /**
   * Threshold in pixels from bottom to hide indicator.
   * @default 15
   */
  scrollThreshold?: number;

  /**
   * Initial delay before checking scroll state (ms).
   * @default 250
   */
  initialDelay?: number;

  /**
   * Custom selector for finding scrollable element.
   * @default "[data-modal-scrollable-content]"
   */
  scrollableSelector?: string;
}

/**
 * Hook for managing scroll indicators in scrollable containers.
 *
 * Automatically shows/hides a scroll indicator based on scroll position
 * and whether the content is scrollable.
 *
 * **Features:**
 * - Auto-detection of scrollable content
 * - Performance-optimized with requestAnimationFrame
 * - Handles max-height scenarios
 * - Responds to content resizes
 * - Hides when scrolled near bottom
 *
 * **Common Use Cases:**
 * - Modal dialogs with scrollable content
 * - Dropdown menus
 * - Long forms in containers
 *
 * @param options Configuration for scroll indicator behavior
 *
 * @example
 * ```tsx
 * const indicatorRef = useRef<HTMLDivElement>(null);
 * const contentRef = useRef<HTMLDivElement>(null);
 *
 * useScrollIndicator({
 *   isActive: isModalOpen,
 *   indicatorRef,
 *   scrollableRef: contentRef,
 *   scrollThreshold: 20,
 *   initialDelay: 300
 * });
 *
 * return (
 *   <div>
 *     <div ref={contentRef} className="scrollable-content">
 *       {/* Long content here *\/}
 *     </div>
 *     <div ref={indicatorRef} className="scroll-indicator">
 *       ? Scroll for more
 *     </div>
 *   </div>
 * );
 * ```
 *
 * @example
 * ```tsx
 * // Using with custom selector
 * useScrollIndicator({
 *   isActive: true,
 *   indicatorRef: myIndicatorRef,
 *   scrollableSelector: '.my-scrollable-content',
 *   scrollThreshold: 10
 * });
 * ```
 */
export function useScrollIndicator({
  isActive,
  indicatorRef,
  scrollableRef,
  scrollThreshold = 15,
  initialDelay = 250,
  scrollableSelector = "[data-modal-scrollable-content]"
}: UseScrollIndicatorOptions): void {
  useEffect(() => {
    if (!isActive || !indicatorRef.current) {
      return;
    }

    const indicator = indicatorRef.current;

    // Find scrollable element - prefer ref, fallback to selector
    const scrollableElement =
      scrollableRef?.current ||
      (document.querySelector(scrollableSelector) as HTMLElement);

    if (!scrollableElement) {
      // Hide indicator if we can't find the scrollable element
      indicator.classList.add("!opacity-0");
      return;
    }

    let ticking = false;

    const updateScrollIndicator = () => {
      const scrollTop = scrollableElement.scrollTop;
      const scrollHeight = scrollableElement.scrollHeight;
      const clientHeight = scrollableElement.clientHeight;

      // Check if content can scroll
      const computedStyle = window.getComputedStyle(scrollableElement);
      const hasMaxHeight = computedStyle.maxHeight && computedStyle.maxHeight !== "none";
      const hasOverflowY =
        computedStyle.overflowY === "auto" || computedStyle.overflowY === "scroll";

      // Determine if there's scrollable content
      const hasScrollableContent =
        scrollHeight > clientHeight ||
        (hasMaxHeight &&
          hasOverflowY &&
          scrollableElement.scrollHeight >= scrollableElement.clientHeight);

      if (!hasScrollableContent) {
        // No overflow - hide the indicator
        indicator.classList.add("!opacity-0");
        return;
      }

      // Show indicator by default when content can scroll
      indicator.classList.remove("!opacity-0");

      // Hide when scrolled near the bottom
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - scrollThreshold;

      if (isNearBottom) {
        indicator.classList.add("!opacity-0");
      } else {
        indicator.classList.remove("!opacity-0");
      }

      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateScrollIndicator);
        ticking = true;
      }
    };

    // Initial check after delay
    const initialTimeout = setTimeout(() => {
      updateScrollIndicator();
    }, initialDelay);

    // Listen for scroll events
    scrollableElement.addEventListener("scroll", handleScroll, { passive: true });

    // Listen for resize events (content changes)
    const resizeObserver = new ResizeObserver(() => {
      updateScrollIndicator();
    });

    resizeObserver.observe(scrollableElement);

    // Cleanup
    return () => {
      clearTimeout(initialTimeout);
      scrollableElement.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
    };
  }, [
    isActive,
    indicatorRef,
    scrollableRef,
    scrollableSelector,
    scrollThreshold,
    initialDelay
  ]);
}
